import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { zodValidator, fallback } from '@tanstack/zod-adapter'
import { agentQueries } from '@/lib/api/queries/agent'
import { userQueries } from '@/lib/api/queries/user'
import {
  useUpdateAgent,
  useTransitionAgent,
  useDeleteAgent,
} from '@/lib/api/mutations/agent'
import { AgentDetailHeader } from '@/components/agent-detail/agent-detail-header'
import { AgentDetailHeaderSkeleton } from '@/components/agent-detail/agent-detail-header-skeleton'
import { AgentStatsBar } from '@/components/agent-detail/agent-stats-bar'
import { AgentStatsBarSkeleton } from '@/components/agent-detail/agent-stats-bar-skeleton'
import { AgentTabs } from '@/components/agent-detail/agent-tabs'
import { StateTransitionButton } from '@/components/agent-detail/state-transition-button'
import { AgentOverflowMenu } from '@/components/agent-detail/agent-overflow-menu'
import { DeleteAgentDialog } from '@/components/agent-detail/delete-agent-dialog'
import { Button } from '@/components/ui/button'
import { MessageSquare, FlaskConical } from 'lucide-react'
import type { AgentStatus } from '@/lib/api/types'

const agentDetailSearch = z.object({
  // 'published' is PRD-agent-only — see agent-tabs.tsx and isPrdAgent().
  // Blog/marketing agents continue to see 'executions'. The route schema
  // accepts both so deep links from either side land cleanly.
  tab: fallback(
    z.enum([
      'overview',
      'prompt',
      'knowledge',
      'access',
      'conversations',
      'analytics',
      'executions',
      'published',
      'drafts',
    ]),
    'overview',
  ).default('overview'),
})

export const Route = createFileRoute('/_authenticated/agents/$agentId')({
  validateSearch: zodValidator(agentDetailSearch),
  component: AgentDetailPage,
})

function AgentDetailPage() {
  const { agentId } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()

  const { data: agent, isLoading } = useQuery(agentQueries.detail(agentId))
  const { data: user } = useQuery(userQueries.me())

  const brandName = user?.brands.find((b) => b.id === agent?.brandId)?.name ?? null
  const brands = user?.brands ?? []

  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const updateMutation = useUpdateAgent(agentId)
  const transitionMutation = useTransitionAgent(agentId)
  const deleteMutation = useDeleteAgent()

  const handleSaveSuccess = () => setIsEditing(false)
  const handleCancel = () => setIsEditing(false)

  const handleTransition = (targetStatus: AgentStatus) => {
    transitionMutation.mutate(targetStatus)
  }

  const handleDelete = () => {
    deleteMutation.mutate(agentId, {
      onSuccess: () => {
        navigate({ to: '/agents' })
      },
    })
  }

  const actionSlot =
    agent && !isEditing ? (
      <>
        {['published', 'draft', 'in_review'].includes(agent.status) && (
          <Button asChild>
            <Link
              to="/chat/$agentId"
              params={{ agentId: agent.id }}
              search={agent.status !== 'published' ? { sandbox: true } : {}}
            >
              {agent.status !== 'published' ? (
                <>
                  <FlaskConical className="w-4 h-4" />
                  Sandbox Chat
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4" />
                  Chat
                </>
              )}
            </Link>
          </Button>
        )}
        <StateTransitionButton
          agent={agent}
          onTransition={handleTransition}
          isPending={transitionMutation.isPending}
        />
        <AgentOverflowMenu
          agent={agent}
          onTransition={handleTransition}
          onDelete={() => setShowDeleteDialog(true)}
          isPending={transitionMutation.isPending}
        />
      </>
    ) : null

  return (
    <div className="h-full flex flex-col">
      {isLoading ? (
        <AgentDetailHeaderSkeleton />
      ) : agent ? (
        <AgentDetailHeader
          agent={agent}
          brandName={brandName}
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onCancel={handleCancel}
          isSaving={updateMutation.isPending}
          activeTab={tab}
          actionSlot={actionSlot}
        />
      ) : null}
      {isLoading ? <AgentStatsBarSkeleton /> : <AgentStatsBar />}
      <AgentTabs
        agent={agent}
        agentId={agentId}
        tab={tab}
        navigate={navigate}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        isLoading={isLoading}
        brands={brands}
        updateMutation={updateMutation}
        onSaveSuccess={handleSaveSuccess}
      />
      {agent && (
        <DeleteAgentDialog
          agentName={agent.name}
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDelete}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  )
}
